import { DismissibleTag, Tag } from '@carbon/react';
import classNames from 'classnames';
import { useEffect, useRef } from 'react';
import styles from './alias-pills.module.scss';

export interface AliasPill {
  id: number;
  name: string;
}

export interface AliasPillsProps {
  aliases: AliasPill[];
  /**
   * Called with the alias whose × was pressed. Leave it out for read-only pills, which have no ×
   * (tables and catalogs show aliases that are managed elsewhere).
   */
  onRemove?: (alias: AliasPill) => void;
  /** Aliases being removed. Their × is disabled so a second press can't send the request twice. */
  removingIds?: ReadonlySet<number>;
  /**
   * Called after the alias whose × was pressed has gone from the list and no pill is left to take
   * focus, so the caller can put it somewhere sensible (the Add Alias button).
   */
  onEmptied?: () => void;
  /** What to show when there are no aliases, for a table cell (`--`). Left out, nothing is shown. */
  emptyText?: string;
  className?: string;
}

/**
 * A record's aliases as gray pills. They wrap onto new lines and never truncate. With
 * `onRemove` each pill has a × labelled with its name; without it the pills are read-only.
 * @param {AliasPillsProps} props - The aliases and, to make them dismissible, the remove handler.
 * @return {JSX.Element | null} The pills, or nothing when there are no aliases.
 */
function AliasPills({
  aliases,
  onRemove,
  removingIds,
  onEmptied,
  emptyText,
  className,
}: AliasPillsProps) {
  const listRef = useRef<HTMLUListElement>(null);
  // The pill whose × was pressed: once it has gone from the list its element is unmounted and focus
  // would fall to <body>, so it moves to the pill that took its place (else the one before it).
  const removing = useRef<{ id: number; index: number } | null>(null);

  useEffect(() => {
    const pending = removing.current;
    if (!pending) return;
    const stillListed = aliases.some((alias) => alias.id === pending.id);
    // The request is still running: the × is disabled and nothing has changed yet.
    if (stillListed && removingIds?.has(pending.id)) return;
    removing.current = null;
    const buttons = listRef.current?.querySelectorAll('button');
    if (stillListed) {
      // The removal failed: the pill stays, and its × was disabled while the DELETE ran, so the browser
      // moved focus to <body>. Put it back, so a keyboard user is not sent to the top of the page.
      buttons?.[pending.index]?.focus();
      return;
    }
    if (!buttons || buttons.length === 0) {
      onEmptied?.();
      return;
    }
    buttons[Math.min(pending.index, buttons.length - 1)].focus();
  }, [aliases, removingIds, onEmptied]);

  if (aliases.length === 0) return emptyText ? <span>{emptyText}</span> : null;

  return (
    <ul
      ref={listRef}
      className={classNames(styles.list, className)}
      aria-label="Aliases"
    >
      {aliases.map((alias, index) => {
        const removeLabel = `Remove alias ${alias.name}`;
        return (
          <li key={alias.id} className={styles.item}>
            {onRemove ? (
              <DismissibleTag
                className={styles.pill}
                data-testid="alias-pill"
                type="gray"
                text={alias.name}
                title={removeLabel}
                dismissTooltipLabel={removeLabel}
                disabled={removingIds?.has(alias.id)}
                onClose={() => {
                  removing.current = { id: alias.id, index };
                  onRemove(alias);
                }}
              />
            ) : (
              <Tag className={styles.pill} data-testid="alias-pill" type="gray">
                {alias.name}
              </Tag>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export { AliasPills };
